import {
  Controller,
  ControllerRenderProps,
  useForm,
  useWatch,
} from 'react-hook-form';
import {
  ConfigureSaleTaxBy,
  DomesticSalesFeeFormData,
  schemaDomesticSalesFee,
} from '@src/types/feeSettings.ts';
import { useCallback } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Input,
  InputSelect,
  TypeSelector,
} from '@features/ui';
import {
  configureSalesTaxData,
  StateOptions,
} from '@features/admin/feeSettings/feeSettingData.ts';
import { ZipCodeForm } from '@features/admin/feeSettings/ZipCodeForm.tsx';
import { yupResolver } from '@hookform/resolvers/yup';

export const CreateDomesticSalesTaxPage = () => {
  const {
    handleSubmit,
    control,
    getValues,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<DomesticSalesFeeFormData>({
    resolver: yupResolver(schemaDomesticSalesFee),
    defaultValues: { configure_sales_tax: ConfigureSaleTaxBy.State },
  });

  const type = useWatch({ control, name: 'configure_sales_tax' });

  const handleSubmitForm = useCallback((data: DomesticSalesFeeFormData) => {
    console.log('data', data);
  }, []);

  const renderAllProductTypeContent = useCallback(() => {
    return (
      <>
        {/* State */}
        <div
          className={'lg:max-w-[500px] w-full mt-10'}
          style={{
            display: type === ConfigureSaleTaxBy.State ? 'block' : 'none',
          }}
        >
          <Controller
            control={control}
            name="state"
            render={({ field }) => (
              <div className={'flex flex-col gap-1'}>
                <p className={'ml-2'}>State</p>
                <InputSelect
                  options={StateOptions}
                  placeholder={'Select'}
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                />
              </div>
            )}
          />
        </div>

        {/* ZipCode */}
        <div
          className={
            'w-full mt-10 lg:flex-row flex-col gap-[81px] items-center'
          }
          style={{
            display: type === ConfigureSaleTaxBy.ZipCode ? 'block' : 'none',
          }}
        >
          <ZipCodeForm control={control} getValues={getValues} reset={reset} />
        </div>
      </>
    );
  }, [type, control, getValues, reset]);

  const handleChangeConfigure = useCallback(
    (
      field: ControllerRenderProps<
        DomesticSalesFeeFormData,
        'configure_sales_tax'
      >,
      val: ConfigureSaleTaxBy
    ) => {
      const currentValue = getValues();

      reset({
        ...currentValue,
        state: undefined,
        zip_code: undefined,
        specific: undefined,
        range_from: undefined,
        range_to: undefined,
      });
      field.onChange(val);
    },
    [getValues, reset]
  );

  return (
    <div>
      <div className={'flex flex-col gap-y-2.5 w-fit'}>
        <p className={'text-[23px] font-medium text-blue'}>
          Domestic Sales Tax
        </p>
        <div className={'h-[2px] w-full bg-blue'} />
      </div>
      <form autoComplete="off" onSubmit={handleSubmit(handleSubmitForm)}>
        <Card className={'text-xs mt-[18px]'}>
          <Controller
            control={control}
            name="configure_sales_tax"
            render={({ field }) => {
              const indexOfType = configureSalesTaxData.findIndex(
                (option) => option.value === field.value
              );

              return (
                <div className={'w-fit'}>
                  <TypeSelector
                    tabActive={indexOfType >= 0 ? indexOfType : 0}
                    tabs={configureSalesTaxData}
                    onChangeTab={(_, item) =>
                      handleChangeConfigure(
                        field,
                        item.value as ConfigureSaleTaxBy
                      )
                    }
                  />
                  <div
                    className={'flex-1 bg-white origin-top'}
                    style={{
                      height: '1px',
                      transform: 'scaleY(0.3)',
                    }}
                  />
                </div>
              );
            }}
          />
          {renderAllProductTypeContent()}
          <div
            className={
              'flex gap-x-[81px] lg:flex-row flex-col gap-y-6 w-full mt-10 items-end'
            }
          >
            <Controller
              control={control}
              name="tax_rate"
              render={({ field }) => (
                <div className={'flex flex-col gap-1 max-w-[500px] flex-1'}>
                  <p>Tax Rate</p>
                  <Input
                    error={!!errors?.tax_rate?.message}
                    placeholder={'Enter %'}
                    textSize={'xs'}
                    type={'number'}
                    variant={'secondary'}
                    {...field}
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name={`apply_tax_to_shipping`}
              render={({ field }) => (
                <div className={'flex-1 lg:pb-2.5'}>
                  <Checkbox
                    checked={!!field.value}
                    label={'Apply rate to shipping amount'}
                    onCheckedChange={(value) => field.onChange(value)}
                  />
                </div>
              )}
            />
          </div>
          <div className={'flex justify-end'}>
            <Button
              className={'mt-[40px] mb-[30px]'}
              loading={isSubmitting}
              size={'sm'}
              variant={'lime'}
            >
              Save
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
};
